import { useEffect, useMemo, useState } from 'react';
import API from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Copy, Loader2, Megaphone, Save } from 'lucide-react';
import { toast } from 'sonner';
import { FlyerPhotos } from './FlyerPhotos';
import { buildFlyerText, copyText } from './flyerUtils';

const FIELDS = ['lead_id', 'property_address', 'property_city', 'property_state', 'property_zip', 'asking_price', 'property_type', 'area_tag', 'headline', 'beds', 'baths', 'sqft', 'lot_sqft', 'highlights', 'notes', 'contact_name', 'contact_phone'];
const numOrNull = v => (v === '' || v === null || v === undefined ? null : Number(v));

function L({ label, children, className = '' }) {
  return <label className={`block space-y-1 ${className}`}><span className="text-[11px] font-medium text-gray-500">{label}</span>{children}</label>;
}

async function initialForm(property, lead) {
  const [s, found] = await Promise.all([
    API.get('/flyer-settings').then(r => r.data).catch(() => ({})),
    API.get('/flyers/lookup', { params: lead ? { lead_id: lead.id, address: property.property_address } : { address: property.property_address } }).then(r => r.data.flyer).catch(() => null),
  ]);
  if (found) return { flyer: found, form: { ...found, highlightsText: (found.highlights || []).join('\n') } };
  const full = lead ? await API.get(`/leads/${lead.id}`).then(r => r.data).catch(() => lead) : null;
  return {
    flyer: null,
    form: {
      lead_id: lead?.id || null, property_address: property.property_address || '', property_city: property.property_city || '', property_state: property.property_state || 'FL',
      property_zip: property.property_zip || '', asking_price: property.asking_price || full?.marketing_price || full?.asking_price || 0, property_type: property.property_type || '',
      area_tag: (property.property_city || '').toUpperCase(), headline: '', beds: full?.bedrooms || '', baths: full?.bathrooms || '', sqft: full?.sqft || '', lot_sqft: full?.lot_size || '',
      highlightsText: '', notes: full?.notes || '', contact_name: s.contact_name || '', contact_phone: s.contact_phone || '',
    },
  };
}

export function FlyerDialog({ open, onOpenChange, property, lead, onUseInCampaign }) {
  const [form, setForm] = useState(null);
  const [flyer, setFlyer] = useState(null);
  const [busy, setBusy] = useState('');
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open || !property) return;
    setForm(null);
    initialForm(property, lead).then(({ flyer: fl, form: fm }) => { setFlyer(fl); setForm(fm); });
  }, [open, property?.property_address, lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = () => {
    const p = Object.fromEntries(FIELDS.map(k => [k, form[k]]));
    return { ...p, highlights: form.highlightsText.split('\n').map(x => x.replace(/^✅\s*/, '').trim()).filter(Boolean), asking_price: Number(form.asking_price) || 0,
      beds: numOrNull(form.beds), baths: numOrNull(form.baths), sqft: numOrNull(form.sqft), lot_sqft: numOrNull(form.lot_sqft), lead_id: form.lead_id || null };
  };
  const save = async () => {
    const body = payload();
    const r = flyer ? await API.put(`/flyers/${flyer.id}`, body) : await API.post('/flyers', body);
    API.put('/flyer-settings', { contact_name: form.contact_name, contact_phone: form.contact_phone }).catch(() => {});
    setFlyer(r.data);
    return r.data;
  };
  const text = useMemo(() => (form ? buildFlyerText({ ...payload(), photo_count: flyer?.photos?.length || 0 }, flyer?.token) : ''), [form, flyer]); // eslint-disable-line react-hooks/exhaustive-deps

  const aiDraft = async () => {
    setBusy('ai');
    try {
      const p = payload();
      const r = await API.post('/flyers/ai-draft', { ...p, beds: p.beds, baths: p.baths });
      setForm(f => ({ ...f, area_tag: r.data.area_tag || f.area_tag, headline: r.data.headline || f.headline, highlightsText: (r.data.highlights || []).join('\n') }));
      toast.success('AI drafted your flyer — edit anything before copying');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI draft failed'); }
    setBusy('');
  };
  const run = (key, fn) => async () => { setBusy(key); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || 'Something went wrong'); } setBusy(''); };
  const doCopy = run('copy', async () => {
    const ok = await copyText(text);
    if (ok) toast.success('Flyer text copied — paste it into your text message'); else toast.error('Copy failed — select the preview text and copy it');
    await save();
  });
  const doSave = run('save', async () => { await save(); toast.success('Flyer saved'); });
  const doUse = run('use', async () => { const f = await save(); onUseInCampaign({ flyer: f, text: buildFlyerText({ ...payload(), photo_count: f.photos?.length || 0 }, f.token) }); onOpenChange(false); });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto bg-white" data-testid="flyer-dialog">
        <DialogHeader><DialogTitle>Deal Flyer</DialogTitle></DialogHeader>
        {!form ? <div className="grid h-60 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0073ea]" /></div> : (
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <L label="Area (top line)"><Input value={form.area_tag} onChange={e => up('area_tag', e.target.value)} data-testid="flyer-area" /></L>
                <L label="Asking price"><Input value={form.asking_price || ''} onChange={e => up('asking_price', e.target.value.replace(/[^\d.]/g, ''))} inputMode="numeric" data-testid="flyer-price" /></L>
              </div>
              <L label="Headline"><Input value={form.headline} onChange={e => up('headline', e.target.value)} placeholder="🎾 PRIVATE TENNIS COURT & POOL HOME! 🏊" data-testid="flyer-headline" /></L>
              <div className="grid grid-cols-4 gap-2">
                <L label="Beds"><Input value={form.beds ?? ''} onChange={e => up('beds', e.target.value)} inputMode="decimal" data-testid="flyer-beds" /></L>
                <L label="Baths"><Input value={form.baths ?? ''} onChange={e => up('baths', e.target.value)} inputMode="decimal" data-testid="flyer-baths" /></L>
                <L label="Living sq ft"><Input value={form.sqft ?? ''} onChange={e => up('sqft', e.target.value.replace(/\D/g, ''))} inputMode="numeric" data-testid="flyer-sqft" /></L>
                <L label="Lot sq ft"><Input value={form.lot_sqft ?? ''} onChange={e => up('lot_sqft', e.target.value.replace(/\D/g, ''))} inputMode="numeric" data-testid="flyer-lot" /></L>
              </div>
              <L label="Notes for AI (features, condition, anything special)"><Textarea rows={2} value={form.notes} onChange={e => up('notes', e.target.value)} placeholder="pool, tennis court, corner lot, 2 car garage, needs renovation" data-testid="flyer-notes" /></L>
              <Button variant="outline" onClick={aiDraft} disabled={busy === 'ai'} className="w-full border-violet-300 text-violet-700" data-testid="flyer-ai-btn">
                {busy === 'ai' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}Write headline & highlights with AI
              </Button>
              <L label="Highlights (one per line)"><Textarea rows={5} value={form.highlightsText} onChange={e => up('highlightsText', e.target.value)} placeholder={'Private Swimming Pool\nSpacious Corner Lot'} data-testid="flyer-highlights" /></L>
              <div className="grid grid-cols-2 gap-2">
                <L label="Your name (saved as default)"><Input value={form.contact_name} onChange={e => up('contact_name', e.target.value)} data-testid="flyer-contact-name" /></L>
                <L label="Your phone"><Input value={form.contact_phone} onChange={e => up('contact_phone', e.target.value)} data-testid="flyer-contact-phone" /></L>
              </div>
              <FlyerPhotos flyer={flyer} ensureSaved={save} address={form.property_address} onChange={photos => setFlyer(f => ({ ...f, photos }))} />
            </div>
            <div className="space-y-3">
              <p className="text-[11px] font-medium text-gray-500">Preview — exactly what gets copied</p>
              <pre className="whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 p-4 font-sans text-sm leading-relaxed text-gray-900" data-testid="flyer-preview">{text}</pre>
              {!flyer?.photos?.length && <p className="text-[11px] text-gray-500">Add photos to include a short photo-gallery link at the bottom.</p>}
              <div className="flex flex-wrap gap-2">
                <Button onClick={doCopy} disabled={!!busy} className="bg-[#0073ea] hover:bg-[#0060c2] text-white" data-testid="flyer-copy-text-btn">{busy === 'copy' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Copy className="h-4 w-4 mr-1.5" />}Copy Text</Button>
                <Button variant="outline" onClick={doSave} disabled={!!busy} data-testid="flyer-save-btn"><Save className="h-4 w-4 mr-1.5" />Save</Button>
                {onUseInCampaign && <Button variant="outline" onClick={doUse} disabled={!!busy} data-testid="flyer-use-campaign-btn"><Megaphone className="h-4 w-4 mr-1.5" />Use in Campaign</Button>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
